import { useEffect, useState } from "react";
import { toast } from "sonner";
import { FolderPlus, Pencil, Trash2, Images, Save, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Panel, BrandButton } from "@/components/PageHeader";
import { Field, Pick } from "@/components/event-setup/BasicsSection";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { api, errMsg } from "@/lib/api";
import { fileSrc } from "@/lib/resources";
import { AlbumPhotos } from "./AlbumPhotos";

const NONE = "__none";
const blank = { title: "", event_id: null, description: "" };

const AlbumFields = ({ f, set, events, prefix }) => <div className="grid sm:grid-cols-2 gap-3">
  <Field label="Album name"><Input value={f.title} maxLength={120} onChange={(e) => set({ ...f, title: e.target.value })} data-testid={`${prefix}-title`} /></Field>
  <Field label="Event (optional)"><Pick value={f.event_id || NONE} onChange={(v) => set({ ...f, event_id: v === NONE ? null : v })} options={[{ value: NONE, label: "Not linked to an event" }, ...events.map((e) => ({ value: e.id, label: e.title }))]} testId={`${prefix}-event`} /></Field>
  <Field label="Description (optional)" className="sm:col-span-2"><Input value={f.description} maxLength={400} onChange={(e) => set({ ...f, description: e.target.value })} data-testid={`${prefix}-description`} /></Field>
</div>;

const AlbumRow = ({ a, events, onChanged, onDelete }) => {
  const [edit, setEdit] = useState(null);
  const [open, setOpen] = useState(false);
  const save = async () => {
    try { await api.put(`/resources/albums/${a.id}`, edit); toast.success("Album updated"); setEdit(null); onChanged(); } catch (e) { toast.error(errMsg(e)); }
  };
  return <li className="py-4" data-testid={`album-row-${a.id}`}>
    {edit ? <div className="space-y-3"><AlbumFields f={edit} set={setEdit} events={events} prefix={`album-edit-${a.id}`} />
      <div className="flex gap-2"><BrandButton onClick={save} disabled={edit.title.trim().length < 2} data-testid={`album-edit-save-${a.id}`}><Save className="w-4 h-4" />Save</BrandButton><BrandButton variant="outline" onClick={() => setEdit(null)} data-testid={`album-edit-cancel-${a.id}`}><X className="w-4 h-4" />Cancel</BrandButton></div></div>
      : <div className="flex items-center gap-3">
        <div className="w-14 h-14 rounded bg-[#F3F4F6] overflow-hidden shrink-0">{a.cover_url ? <img src={fileSrc(a.cover_url)} alt="" className="w-full h-full object-cover" /> : <Images className="w-5 h-5 m-4 text-gray-400" />}</div>
        <div className="min-w-0 flex-1"><div className="font-semibold">{a.title}</div><div className="text-xs text-gray-500">{a.event_title || "No event"} · {a.photo_count} photo{a.photo_count === 1 ? "" : "s"}</div></div>
        <div className="flex flex-wrap justify-end gap-3 text-xs font-semibold">
          <button onClick={() => setOpen((v) => !v)} className="inline-flex items-center gap-1 text-[#E60000] hover:underline" data-testid={`album-photos-btn-${a.id}`}><Images className="w-3.5 h-3.5" />{open ? "Hide photos" : "Photos"}</button>
          <button onClick={() => setEdit({ title: a.title, event_id: a.event_id, description: a.description || "" })} className="inline-flex items-center gap-1 hover:text-[#E60000]" data-testid={`album-edit-btn-${a.id}`}><Pencil className="w-3.5 h-3.5" />Edit</button>
          <button onClick={() => onDelete(a)} className="inline-flex items-center gap-1 text-[#B30000] hover:underline" data-testid={`album-delete-btn-${a.id}`}><Trash2 className="w-3.5 h-3.5" />Delete</button>
        </div>
      </div>}
    {open && <AlbumPhotos album={a} onChanged={onChanged} />}
  </li>;
};

export const AlbumsManager = ({ albums, onChanged }) => {
  const [f, setF] = useState(blank);
  const [events, setEvents] = useState([]);
  const [busy, setBusy] = useState(false);
  const [del, setDel] = useState(null);
  useEffect(() => { api.get("/resources/event-options").then((r) => setEvents(r.data)).catch(() => {}); }, []);
  const create = async () => {
    setBusy(true);
    try { await api.post("/resources/albums", f); toast.success("Album created. Open Photos to add pictures."); setF(blank); onChanged(); } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  return <Panel title="Gallery albums" testId="albums-manager">
    <div className="space-y-3" data-testid="album-add-form">
      <AlbumFields f={f} set={setF} events={events} prefix="album-add" />
      <BrandButton onClick={create} disabled={busy || f.title.trim().length < 2} data-testid="album-add-save"><FolderPlus className="w-4 h-4" />Create album</BrandButton>
    </div>
    <ul className="divide-y mt-6 border-t" data-testid="albums-list">
      {albums.length ? albums.map((a) => <AlbumRow key={a.id} a={a} events={events} onChanged={onChanged} onDelete={setDel} />) : <li className="py-4 text-sm text-gray-500">No albums yet.</li>}
    </ul>
    <ConfirmDialog open={!!del} onClose={() => setDel(null)} title="Delete this album?" description={`"${del?.title}" and its ${del?.photo_count || 0} photo(s) will be removed from the Gallery.`} confirmLabel="Delete album" testId="album-delete"
      onConfirm={async () => { await api.delete(`/resources/albums/${del.id}`); toast.success("Album deleted"); onChanged(); }} />
  </Panel>;
};
